import React, { useMemo, useRef, useState, useEffect, useCallback } from "react";
import { Box, Typography, Popover, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import AutoAwesomeOutlinedIcon from "@mui/icons-material/AutoAwesomeOutlined";
import { LuChevronDown, LuCheck } from "react-icons/lu";
import { DataTableCard, M3Button } from "../../../components/m3";
import CircularityTableActions from "../../../circularity-components/CircularityTableActions";
import ColumnMenu from "../../../components/context-menu/ColumnMenu";
import AgGrid from "../../../components/grid/AgGrid";
import {
  defaultColDef,
  floatFormater,
  leftAlignColDef,
  rightAlignColDef,
} from "../../../components/grid/columnDefinitions/columnDefinitions";
import { useContextProvider } from "../../../components/page-context/ContextProvider";
import {
  DISASSEMBLY_EASE_OPTIONS,
  getDisassemblyEaseOption,
  getDefaultDisassemblyFactors,
} from "./pciOptions";
import { recalculate_row_mci } from "../../../functions/CircularityFunctions";
import { isCircularityLocked, isCircularityRowEditable } from "../../../functions/circularityLock";
import { Circularity } from "../../../db_access/circularity";
import { ORG_PALETTE, getOrgTokens } from "../../../theme/style/organizationPalette";
import { getM3Colors, getM3Elevation } from "../../../theme/style/m3Palette";

// Right-aligned numeric input cells; locked rows (paints, reagents) are greyed out.
const lockedCellStyle = (params) => {
  if (!isCircularityLocked(params.data)) return rightAlignColDef.cellStyle;
  const tok = getOrgTokens(Boolean(params.context?.isDark));
  return {
    ...rightAlignColDef.cellStyle,
    color: tok.text.muted,
    backgroundColor: tok.bg.tableHeader,
    cursor: "not-allowed",
  };
};

/**
 * Modern SaaS-grade Disassembly Ease Pill Cell Renderer
 * Click opens a rich, wide Material UI Popover selector with factor metrics.
 */
const DisassemblyEaseCellRenderer = (params) => {
  const isDark = params.isDark ?? params.context?.isDark;
  const [anchorEl, setAnchorEl] = useState(null);
  const { t } = useTranslation();

  if (!params.data) return null;

  const currentOption =
    typeof params.value === "object" && params.value !== null
      ? params.value
      : (typeof params.data?.disassembly_ease === "object" && params.data?.disassembly_ease !== null
          ? params.data.disassembly_ease
          : getDisassemblyEaseOption(
              params.data?.ct_factor,
              params.data?.ca_factor,
              params.data?.indep_factor,
              params.data?.gpe_factor
            ));

  const isLocked = isCircularityLocked(params.data);

  const handleClick = (e) => {
    e.stopPropagation();
    if (isLocked) return;
    setAnchorEl(e.currentTarget);
  };

  const handleClose = (e) => {
    if (e) e.stopPropagation();
    setAnchorEl(null);
  };

  const handleSelect = (option, e) => {
    if (e) e.stopPropagation();
    setAnchorEl(null);
    if (params.data) {
      params.data.ct_factor = option.ct;
      params.data.ca_factor = option.ca;
      params.data.indep_factor = option.indep;
      params.data.gpe_factor = option.gpe;
      params.data.disassembly_ease = option;
    }
    if (params.node && typeof params.node.setDataValue === "function") {
      params.node.setDataValue("disassembly_ease", option);
    }
    const onChange = params.handleDisassemblyEaseChange || params.context?.handleDisassemblyEaseChange;
    if (onChange) {
      onChange(params.data, option);
    }
  };

  const open = Boolean(anchorEl);
  const m3 = getM3Colors(Boolean(isDark));
  const elevation = getM3Elevation(Boolean(isDark));
  const textSx = {
    fontSize: ORG_PALETTE.font.size.caption,
    fontFamily: ORG_PALETTE.font.family,
    textAlign: "left",
  };

  return (
    <>
      <Box
        onClick={handleClick}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
          width: "100%",
          px: 1.5,
          py: 0.6,
          borderRadius: ORG_PALETTE.radius.input,
          bgcolor: m3.surfaceContainerLowest,
          color: m3.onSurface,
          border: `1px solid ${m3.outlineVariant}`,
          cursor: isLocked ? "not-allowed" : "pointer",
          opacity: isLocked ? 0.55 : 1,
          userSelect: "none",
          transition: "border-color 0.15s ease, background-color 0.15s ease",
          "&:hover": isLocked ? undefined : {
            borderColor: m3.primary,
            bgcolor: m3.stateHover,
          },
        }}
      >
        <Typography
          sx={{
            ...textSx,
            fontWeight: ORG_PALETTE.font.weight.medium,
            color: "inherit",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            flex: 1,
          }}
        >
          {t(currentOption.label)}
        </Typography>
        <LuChevronDown
          size={15}
          style={{
            flexShrink: 0,
            color: m3.onSurfaceVariant,
            transform: open ? "rotate(180deg)" : "rotate(0deg)",
            transition: "transform 0.2s ease",
          }}
        />
      </Box>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        PaperProps={{
          sx: {
            mt: 0.75,
            width: 380,
            maxWidth: "92vw",
            borderRadius: ORG_PALETTE.radius.card,
            border: `1px solid ${m3.outlineVariant}`,
            bgcolor: m3.surfaceContainer,
            backgroundImage: "none",
            boxShadow: elevation[2],
            p: 1,
          },
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }} role="listbox">
          {DISASSEMBLY_EASE_OPTIONS.map((opt) => {
            const isSelected = opt.value === currentOption.value;
            return (
              <Box
                key={opt.value}
                role="option"
                aria-selected={isSelected}
                onClick={(e) => handleSelect(opt, e)}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  px: 1.5,
                  py: 1.1,
                  borderRadius: ORG_PALETTE.radius.input,
                  cursor: "pointer",
                  bgcolor: isSelected ? m3.secondaryContainer : "transparent",
                  color: isSelected ? m3.onSecondaryContainer : m3.onSurface,
                  transition: "background-color 0.15s ease",
                  "&:hover": { bgcolor: isSelected ? m3.secondaryContainer : m3.stateHover },
                }}
              >
                <Typography
                  sx={{
                    ...textSx,
                    fontWeight: isSelected ? ORG_PALETTE.font.weight.semibold : ORG_PALETTE.font.weight.medium,
                    color: "inherit",
                    lineHeight: 1.3,
                    flex: 1,
                    pr: 1,
                  }}
                >
                  {t(opt.label)}
                </Typography>
                {isSelected && <LuCheck size={16} strokeWidth={3} style={{ flexShrink: 0, color: m3.primary }} />}
              </Box>
            );
          })}
        </Box>
      </Popover>
    </>
  );
};

/**
 * Consolidated Material Composition Master Table
 * Groups columns into LCA Assessment, Inputs, DfD (Disassembly ease), and Output.
 * Provides real-time two-way synchronization with all circularity sub-tables.
 */
const MaterialCompositionTable = ({
  combined_data,
  circularities = [],
  setCircularities,
  materials = [],
  aiLoading = false,
  handleSmartFill = null,
  type = "Product",
  id = "default",
  activeTab,
  tabValue,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const { t } = useTranslation();
  const { columnVisibilities, setIsSaveVisible } = useContextProvider();
  const colVisibility_key = "material_composition_table_column_visibility";
  const gridHeaderContainerRef = useRef(null);

  const effectiveMaterials = useMemo(() => {
    if (combined_data?.combined_circularities && combined_data.combined_circularities.length > 0) {
      return combined_data.combined_circularities;
    }
    if (materials && materials.length > 0) {
      return materials.map((m) => {
        const c = circularities?.find(
          (circ) => String(circ.env_id) === String(m.id) || String(circ.id) === String(m.id)
        );
        return { ...m, ...(c || {}) };
      });
    }
    return [];
  }, [combined_data?.combined_circularities, materials, circularities]);

  const massSum = useMemo(() => combined_data?.mass_sum || 0, [combined_data]);
  const [rows, setRows] = useState([]);
  const [columnMenuVisible, setColumnMenuVisible] = useState(false);

  useEffect(() => {
    if (activeTab && tabValue && activeTab !== tabValue) {
      setColumnMenuVisible(false);
    }
  }, [activeTab, tabValue]);

  // Sync rows from materials/combined_data
  useEffect(() => {
    // Read any persisted PCI / disassembly factors from localStorage
    const storageKey = `arivu_pci_selections_${type || "Product"}_${id || "default"}`;
    let savedPciConfigs = {};
    try {
      const saved = localStorage.getItem(storageKey);
      if (saved) savedPciConfigs = JSON.parse(saved);
    } catch (e) {
      console.error("Failed to read PCI configs", e);
    }

    setRows(
      effectiveMaterials.map((material) => {
        const matEnvId = String(material.env_id || material.id);
        const matPkId = String(material.id || material.env_id);
        const pciCfg = savedPciConfigs[matEnvId] || savedPciConfigs[matPkId] || {};
        const dfdDefaults = getDefaultDisassemblyFactors(material);
        const ct = (material.ct_factor !== undefined && material.ct_factor !== null) ? material.ct_factor : (pciCfg.ct !== undefined ? pciCfg.ct : dfdDefaults.ct);
        const ca = (material.ca_factor !== undefined && material.ca_factor !== null) ? material.ca_factor : (pciCfg.ca !== undefined ? pciCfg.ca : dfdDefaults.ca);
        const indep = (material.indep_factor !== undefined && material.indep_factor !== null) ? material.indep_factor : (pciCfg.indep !== undefined ? pciCfg.indep : dfdDefaults.indep);
        const gpe = (material.gpe_factor !== undefined && material.gpe_factor !== null) ? material.gpe_factor : (pciCfg.gpe !== undefined ? pciCfg.gpe : dfdDefaults.gpe);
        const disassembly_ease = (typeof material.disassembly_ease === "object" && material.disassembly_ease !== null) ? material.disassembly_ease : getDisassemblyEaseOption(ct, ca, indep, gpe);

        return {
          ...material,
          ct_factor: ct,
          ca_factor: ca,
          indep_factor: indep,
          gpe_factor: gpe,
          disassembly_ease,
          renewablePct: material.renewablePct ?? 0,
          source: material.source ?? "ArivuDB",
        };
      })
    );
  }, [effectiveMaterials, type, id]);

  const handleDisassemblyEaseChange = useCallback(
    (rowItem, opt) => {
      const materialId = rowItem.env_id || rowItem.id;
      const updatedRow = {
        ...rowItem,
        ct_factor: opt.ct,
        ca_factor: opt.ca,
        indep_factor: opt.indep,
        gpe_factor: opt.gpe,
        disassembly_ease: opt,
      };

      // Persist to localStorage for DesignAssessmentPanel & computePciData
      const storageKey = `arivu_pci_selections_${type || "Product"}_${id || "default"}`;
      try {
        const saved = JSON.parse(localStorage.getItem(storageKey) || "{}");
        const entry = {
          ct: opt.ct,
          ca: opt.ca,
          indep: opt.indep,
          gpe: opt.gpe,
        };
        if (rowItem.id) saved[String(rowItem.id)] = entry;
        if (rowItem.env_id) saved[String(rowItem.env_id)] = entry;
        if (materialId) saved[String(materialId)] = entry;
        localStorage.setItem(storageKey, JSON.stringify(saved));
      } catch (e) {
        console.error("Failed to save disassembly ease to localStorage", e);
      }

      // Recalculate row MCI
      const recalculatedRow = recalculate_row_mci(updatedRow);

      // Update local table rows state
      setRows((prev) =>
        prev.map((r) => ((String(r.env_id || r.id) === String(materialId) || (rowItem.env_id && String(r.env_id) === String(rowItem.env_id))) ? { ...recalculatedRow, disassembly_ease: opt } : r))
      );

      // Two-way synchronization with parent circularities state
      if (setCircularities && Array.isArray(circularities)) {
        const updatedCircs = circularities.map((c) => {
          if (String(c.env_id) === String(materialId) || String(c.id) === String(materialId) || (rowItem.env_id && String(c.env_id) === String(rowItem.env_id)) || (rowItem.id && String(c.id) === String(rowItem.id))) {
            return {
              ...c,
              ...recalculatedRow,
              ct_factor: opt.ct,
              ca_factor: opt.ca,
              indep_factor: opt.indep,
              gpe_factor: opt.gpe,
              disassembly_ease: opt,
              id: c.id,
              env_id: c.env_id || materialId,
            };
          }
          return c;
        });
        setCircularities(updatedCircs);
      }

      // Immediate API call to persist DfD factors to database
      const { disassembly_ease: _de, ...restRow } = recalculatedRow;
      const payloadItem = {
        ...restRow,
        id: !isNaN(Number(rowItem.id)) ? Number(rowItem.id) : rowItem.id,
        env_id: rowItem.env_id || materialId,
        ct_factor: opt.ct,
        ca_factor: opt.ca,
        indep_factor: opt.indep,
        gpe_factor: opt.gpe,
      };
      Circularity.update_input_materials([payloadItem]).catch((err) =>
        console.error("Auto-sync DfD failed", err)
      );

      if (setIsSaveVisible) {
        setIsSaveVisible(true);
      }
    },
    [type, id, circularities, setCircularities, setIsSaveVisible]
  );

  const onCellValueChanged = (params) => {
    const { field } = params.colDef;
    const { data, value } = params;
    const materialId = data.env_id || data.id;

    let updatedRow = { ...data };

    if (field === "uf_product_life") {
      const parsed = parseFloat(value);
      updatedRow.uf_product_life = isNaN(parsed) ? 20 : Math.max(0, parsed);
    } else if (field === "im_recycled_content") {
      const parsed = parseFloat(value);
      const clamped = isNaN(parsed) ? 0 : Math.max(0, Math.min(100, parsed));
      updatedRow.im_recycled_content = clamped;
      const iru = parseFloat(updatedRow.im_reuse) || 0;
      const irm = parseFloat(updatedRow.im_remanufactured) || 0;
      const ibm = parseFloat(updatedRow.im_bio_materials) || 0;
      updatedRow.im_virgin_content = Math.max(0, 100 - (clamped + iru + irm + ibm));
    } else if (field === "im_reuse") {
      const parsed = parseFloat(value);
      const clamped = isNaN(parsed) ? 0 : Math.max(0, Math.min(100, parsed));
      updatedRow.im_reuse = clamped;
      const irc = parseFloat(updatedRow.im_recycled_content) || 0;
      const irm = parseFloat(updatedRow.im_remanufactured) || 0;
      const ibm = parseFloat(updatedRow.im_bio_materials) || 0;
      updatedRow.im_virgin_content = Math.max(0, 100 - (irc + clamped + irm + ibm));
    } else if (field === "disassembly_ease") {
      const opt =
        typeof value === "object" && value !== null
          ? value
          : DISASSEMBLY_EASE_OPTIONS.find((o) => o.value === value) || DISASSEMBLY_EASE_OPTIONS[1];
      updatedRow.ct_factor = opt.ct;
      updatedRow.ca_factor = opt.ca;
      updatedRow.indep_factor = opt.indep;
      updatedRow.gpe_factor = opt.gpe;
      updatedRow.disassembly_ease = opt;

      // Persist to localStorage for DesignAssessmentPanel & computePciData
      const storageKey = `arivu_pci_selections_${type || "Product"}_${id || "default"}`;
      try {
        const saved = JSON.parse(localStorage.getItem(storageKey) || "{}");
        const entry = {
          ct: opt.ct,
          ca: opt.ca,
          indep: opt.indep,
          gpe: opt.gpe,
        };
        if (data.id) saved[String(data.id)] = entry;
        if (data.env_id) saved[String(data.env_id)] = entry;
        if (materialId) saved[String(materialId)] = entry;
        localStorage.setItem(storageKey, JSON.stringify(saved));
      } catch (e) {
        console.error("Failed to save disassembly ease to localStorage", e);
      }
    } else if (field === "om_recycling") {
      const parsed = parseFloat(value);
      const clamped = isNaN(parsed) ? 0 : Math.max(0, Math.min(100, parsed));
      updatedRow.om_recycling = clamped;
      const ou = parseFloat(updatedRow.om_reuse) || 0;
      const obc = parseFloat(updatedRow.om_bio_composted) || 0;
      const obe = parseFloat(updatedRow.om_bio_energy_recovery) || 0;
      updatedRow.om_waste_content = Math.max(0, 100 - (clamped + ou + obc + obe));
    } else if (field === "om_reuse") {
      const parsed = parseFloat(value);
      const clamped = isNaN(parsed) ? 0 : Math.max(0, Math.min(100, parsed));
      updatedRow.om_reuse = clamped;
      const or = parseFloat(updatedRow.om_recycling) || 0;
      const obc = parseFloat(updatedRow.om_bio_composted) || 0;
      const obe = parseFloat(updatedRow.om_bio_energy_recovery) || 0;
      updatedRow.om_waste_content = Math.max(0, 100 - (or + clamped + obc + obe));
    } else if (field === "renewablePct") {
      const parsed = parseFloat(value);
      updatedRow.renewablePct = isNaN(parsed) ? 0 : Math.max(0, Math.min(100, parsed));
    }

    // Recalculate row MCI
    const recalculatedRow = recalculate_row_mci(updatedRow);

    // Update params.data in-place so AG Grid cell retains the new value immediately
    if (params.data) {
      Object.assign(params.data, recalculatedRow);
    }

    const isSameRow = (r) => {
      const matchId = String(data.id || "");
      const matchEnvId = String(data.env_id || "");
      const rId = String(r.id || "");
      const rEnvId = String(r.env_id || "");
      return (
        (matchEnvId && (rEnvId === matchEnvId || rId === matchEnvId)) ||
        (matchId && (rId === matchId || rEnvId === matchId)) ||
        (materialId && (rId === String(materialId) || rEnvId === String(materialId)))
      );
    };

    // Update local table rows state
    setRows((prev) =>
      prev.map((r) => (isSameRow(r) ? { ...r, ...recalculatedRow } : r))
    );

    // Two-way synchronization with parent circularities state
    if (setCircularities && Array.isArray(circularities)) {
      const updatedCircs = circularities.map((c) => {
        if (isSameRow(c)) {
          return {
            ...c,
            ...recalculatedRow,
            id: c.id,
            env_id: c.env_id || data.env_id || materialId,
          };
        }
        return c;
      });
      setCircularities(updatedCircs);
    }

    // Immediate API trigger
    const { disassembly_ease: _de, ...restRow } = recalculatedRow;
    const payloadItem = {
      ...restRow,
      id: !isNaN(Number(data.id)) ? Number(data.id) : data.id,
      env_id: data.env_id || materialId,
      om_recycling: updatedRow.om_recycling,
      om_reuse: updatedRow.om_reuse,
      om_waste_content: updatedRow.om_waste_content,
      om_landfill_waste: updatedRow.om_landfill_waste,
      om_bio_composted: updatedRow.om_bio_composted || 0,
      om_bio_energy_recovery: updatedRow.om_bio_energy_recovery || 0,
      im_recycled_content: updatedRow.im_recycled_content,
      im_reuse: updatedRow.im_reuse,
      im_virgin_content: updatedRow.im_virgin_content,
      uf_product_life: updatedRow.uf_product_life,
    };

    if (field === "uf_product_life") {
      Circularity.update_utilities([payloadItem]).catch((err) => console.error("Update utilities failed", err));
    } else if (field === "im_recycled_content" || field === "im_reuse" || field === "disassembly_ease") {
      Circularity.update_input_materials([payloadItem]).catch((err) => console.error("Update input materials failed", err));
    } else if (field === "om_recycling" || field === "om_reuse") {
      Circularity.update_output_materials([payloadItem]).catch((err) => console.error("Update output materials failed", err));
    }

    if (setIsSaveVisible) {
      setIsSaveVisible(true);
    }
  };

  const colDefs = useMemo(() => {
    const isHidden = (field) => {
      if (!columnVisibilities[colVisibility_key]) return true; // Default hide additional columns unless enabled
      return !columnVisibilities[colVisibility_key].includes(field);
    };

    return [
      {
        headerName: t("LCA Assessment"),
        headerClass: "ag-center-aligned-header ag-lca-group-header",
        children: [
          {
            headerName: t("Sl. No."),
            valueGetter: (params) => {
              if (params.node.rowPinned === "bottom") return "";
              return params.node.rowIndex + 1;
            },
            width: 75,
            minWidth: 60,
            headerClass: "ag-center-aligned-header",
            cellStyle: { display: "flex", alignItems: "center", justifyContent: "center" },
            suppressMenu: true,
            sortable: false,
            field: "si_no",
          },
          {
            headerName: t("Description"),
            field: "description",
            ...leftAlignColDef,
            headerClass: "ag-center-aligned-header",
            flex: 1.2,
            minWidth: 160,
          },
          {
            headerName: t("Mass (kg)"),
            field: "mass",
            ...rightAlignColDef,
            ...floatFormater,
            headerClass: "ag-center-aligned-header ag-lca-boundary",
            cellClass: "ag-cell-lca-boundary",
            minWidth: 100,
          },
        ],
      },
      {
        headerName: t("Inputs"),
        headerClass: "ag-center-aligned-header ag-inputs-group-header",
        children: [
          {
            headerName: t("Service Life (yrs)"),
            field: "uf_product_life",
            ...rightAlignColDef,
            ...floatFormater,
            headerClass: "ag-center-aligned-header",
            editable: isCircularityRowEditable,
            cellStyle: lockedCellStyle,
            minWidth: 135,
          },
          {
            headerName: t("Recycle Content (%)"),
            field: "im_recycled_content",
            ...rightAlignColDef,
            ...floatFormater,
            headerClass: "ag-center-aligned-header",
            editable: isCircularityRowEditable,
            cellStyle: lockedCellStyle,
            minWidth: 145,
          },
          {
            headerName: t("Reuse (%)"),
            field: "im_reuse",
            ...rightAlignColDef,
            ...floatFormater,
            headerClass: "ag-center-aligned-header ag-inputs-boundary",
            cellClass: "ag-cell-inputs-boundary",
            editable: isCircularityRowEditable,
            cellStyle: lockedCellStyle,
            minWidth: 110,
          },
        ],
      },
      {
        headerName: t("Design for Disassembly (DfD)"),
        headerClass: "ag-center-aligned-header ag-dfd-group-header",
        children: [
          {
            headerName: t("Disassembly ease"),
            field: "disassembly_ease",
            cellRenderer: DisassemblyEaseCellRenderer,
            cellRendererParams: {
              isDark,
              handleDisassemblyEaseChange,
            },
            editable: false,
            width: 360,
            minWidth: 320,
            flex: 1.5,
            headerClass: "ag-center-aligned-header ag-dfd-boundary",
            cellClass: "ag-cell-dfd-boundary",
            valueGetter: (params) => {
              if (!params.data) return null;
              if (params.data.disassembly_ease && typeof params.data.disassembly_ease === "object") {
                return params.data.disassembly_ease;
              }
              return getDisassemblyEaseOption(
                params.data.ct_factor,
                params.data.ca_factor,
                params.data.indep_factor,
                params.data.gpe_factor
              );
            },
          },
        ],
      },
      {
        headerName: t("Output"),
        headerClass: "ag-center-aligned-header ag-output-group-header",
        children: [
          {
            headerName: t("Sent to Recycling (%)"),
            field: "om_recycling",
            ...rightAlignColDef,
            ...floatFormater,
            headerClass: "ag-center-aligned-header",
            editable: isCircularityRowEditable,
            cellStyle: lockedCellStyle,
            minWidth: 155,
          },
          {
            headerName: t("Sent for Reusing (%)"),
            field: "om_reuse",
            ...rightAlignColDef,
            ...floatFormater,
            headerClass: "ag-center-aligned-header ag-output-boundary",
            cellClass: "ag-cell-output-boundary",
            editable: isCircularityRowEditable,
            cellStyle: lockedCellStyle,
            minWidth: 145,
          },
        ],
      },
      {
        headerName: t("Additional Indicators"),
        headerClass: "ag-center-aligned-header ag-additional-group-header",
        children: [
          {
            headerName: t("Normalized Mass (%)"),
            field: "normalized_mass",
            ...rightAlignColDef,
            ...floatFormater,
            headerClass: "ag-center-aligned-header",
            hide: isHidden("normalized_mass"),
          },
          {
            headerName: t("GWP (kg CO₂e)"),
            field: "gwp_total",
            ...rightAlignColDef,
            ...floatFormater,
            headerClass: "ag-center-aligned-header",
            hide: isHidden("gwp_total"),
          },
          {
            headerName: t("Normalized GWP"),
            field: "normalized_gwp",
            ...rightAlignColDef,
            ...floatFormater,
            headerClass: "ag-center-aligned-header",
            hide: isHidden("normalized_gwp"),
          },
          {
            headerName: t("Renewable Content (%)"),
            field: "renewablePct",
            ...rightAlignColDef,
            ...floatFormater,
            headerClass: "ag-center-aligned-header",
            editable: true,
            minWidth: 170,
            hide: isHidden("renewablePct"),
          },
          {
            headerName: t("Data Source"),
            field: "source",
            ...leftAlignColDef,
            headerClass: "ag-center-aligned-header",
            minWidth: 140,
            hide: isHidden("source"),
          },
        ],
      },
    ];
  }, [columnVisibilities, colVisibility_key, t, isDark, handleDisassemblyEaseChange]);

  const flatColDefs = useMemo(() => {
    const list = [];
    colDefs.forEach((group) => {
      if (group.children) {
        group.children.forEach((c) => list.push(c));
      } else {
        list.push(group);
      }
    });
    return list;
  }, [colDefs]);

  const defaultCols = useMemo(
    () => [
      "si_no",
      "description",
      "mass",
      "uf_product_life",
      "im_recycled_content",
      "im_reuse",
      "disassembly_ease",
      "om_recycling",
      "om_reuse",
    ],
    []
  );

  return (
    <DataTableCard
      title={t("Material Composition")}
      actions={
        <CircularityTableActions
          columnsAnchorRef={gridHeaderContainerRef}
          onToggleColumns={() => setColumnMenuVisible((p) => !p)}
        >
          {handleSmartFill && (
            <M3Button
              size="sm"
              variant="outlined"
              icon={<AutoAwesomeOutlinedIcon sx={{ fontSize: 18 }} />}
              loading={aiLoading}
              onClick={() => handleSmartFill(circularities, effectiveMaterials)}
              disabled={!effectiveMaterials || effectiveMaterials.length === 0}
              disabledReason={t("Add materials to estimate circularity values")}
            >
              {aiLoading ? t("Estimating...") : t("Smart Fill with AI")}
            </M3Button>
          )}
        </CircularityTableActions>
      }
    >
      <Box sx={{ height: "auto" }}>
        <AgGrid
          columnDefs={colDefs}
          rowData={rows}
          getRowId={(params) => String(params.data?.env_id || params.data?.id)}
          onCellValueChanged={onCellValueChanged}
          headerCheckbox={false}
          context={{ isDark, handleDisassemblyEaseChange }}
          footerLeftContent={
            <Typography variant="List Title" sx={{ fontSize: "14px", fontWeight: 700, color: "text.primary" }}>
              {t("Total Mass")} (kg): {massSum.toFixed(2)}
            </Typography>
          }
        />
      </Box>

      <ColumnMenu
        anchorRef={gridHeaderContainerRef}
        visible={columnMenuVisible}
        onClose={() => setColumnMenuVisible(false)}
        colDefs={flatColDefs}
        colVisibility_key={colVisibility_key}
        defaultCols={defaultCols}
      />
    </DataTableCard>
  );
};

export default MaterialCompositionTable;
